'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

const DEFAULT_TTL_DAYS = 30;
const MAX_TTL_DAYS = 365;
const COOKIE_DOMAIN = '.uvi.ru';
const DEFAULT_TRACKING_DOMAIN = 'https://trckcppro.com';
const RAFINAD_GOAL = 'paid-order-uvi';
const RAFINAD_CURRENCY = 'RUB';
const RAFINAD_NEW_ORDER_STATUS = '1';
const ORDER_TRACKING_STORAGE_KEY = 'rafinad:order-tracking';

const RAFINAD_COOKIE_NAMES = [
  'utm_source',
  'utm_content',
  'utm_medium',
  'utm_campaign',
  'click_id',
  'click_id_xg',
  'flow_id',
  'flow_id_xg',
  'domain_xg',
  'attribution_xg'
] as const;

const RAFINAD_SOURCES = new Set([
  'rafinad',
  'gdeslon',
  'gde_slon',
  'gde-slon',
  'gde slon',
  'xg'
]);
const PAID_MEDIUMS = new Set([
  'affiliate',
  'cpa',
  'cpc',
  'cpm',
  'display',
  'paid',
  'paid_social',
  'partner',
  'ppc',
  'retargeting'
]);
const PAID_SOURCES = new Set([
  'admitad',
  'cityads',
  'facebook',
  'google',
  'mytarget',
  'vk',
  'yandex'
]);

type RafinadPageType = 'main' | 'list' | 'card' | 'basket' | 'thanks' | 'other';

type RafinadFlow = {
  page_type: RafinadPageType;
  order_id?: string;
  order_total?: string;
  currency?: string;
  goal?: string;
  status?: string;
};

type RafinadOrderTrackingParams = {
  orderId: string;
  orderUuid?: string;
  orderTotal?: number | string;
  currency?: string;
  goal?: string;
  status?: string;
};

type RafinadWindow = Window & {
  XG_FLOW?: RafinadFlow;
};

const normalize = (value: string | null) => value?.trim().toLowerCase() || '';

const firstParam = (params: URLSearchParams, names: string[]) => {
  for (const name of names) {
    const value = params.get(name)?.trim();

    if (value) {
      return value;
    }
  }

  return '';
};

const getCookie = (name: string) => {
  const value = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1);

  return value ? decodeURIComponent(value) : '';
};

const getTtlDays = (params: URLSearchParams) => {
  const days = Number(params.get('clt') || DEFAULT_TTL_DAYS);

  if (!Number.isFinite(days) || days <= 0) {
    return DEFAULT_TTL_DAYS;
  }

  return Math.min(Math.floor(days), MAX_TTL_DAYS);
};

const getCookieDomain = () => {
  const hostname = window.location.hostname;

  if (hostname === 'uvi.ru' || hostname.endsWith('.uvi.ru')) {
    return COOKIE_DOMAIN;
  }

  return '';
};

const getCookieParts = (days: number, domain: string) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const parts = [
    `path=/`,
    `max-age=${days * 24 * 60 * 60}`,
    `expires=${expires}`,
    'SameSite=Lax'
  ];

  if (domain) {
    parts.push(`domain=${domain}`);
  }

  if (window.location.protocol === 'https:') {
    parts.push('Secure');
  }

  return parts;
};

const setCookie = (name: string, value: string, days: number, domain: string) => {
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    ...getCookieParts(days, domain)
  ].join(';');
};

const deleteCookie = (name: string, domain: string) => {
  const parts = [
    'path=/',
    'max-age=0',
    'expires=Thu, 01 Jan 1970 00:00:00 GMT',
    'SameSite=Lax'
  ];

  if (domain) {
    parts.push(`domain=${domain}`);
  }

  document.cookie = `${name}=;${parts.join(';')}`;
};

const clearRafinadCookies = (domain: string) => {
  RAFINAD_COOKIE_NAMES.forEach((name) => {
    deleteCookie(name, '');

    if (domain) {
      deleteCookie(name, domain);
    }
  });
};

const getPageType = (pathname: string): RafinadPageType => {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';

  if (normalizedPathname === '/') {
    return 'main';
  }

  if (normalizedPathname === '/checkout/success') {
    return 'thanks';
  }

  if (normalizedPathname === '/cart' || normalizedPathname === '/checkout') {
    return 'basket';
  }

  if (normalizedPathname.startsWith('/product/')) {
    return 'card';
  }

  if (
    normalizedPathname.startsWith('/catalog') ||
    normalizedPathname.startsWith('/collection') ||
    normalizedPathname.startsWith('/collections') ||
    normalizedPathname.startsWith('/sale') ||
    normalizedPathname.startsWith('/search')
  ) {
    return 'list';
  }

  return 'other';
};

const getStoredOrderTracking = (orderUuid: string | null): RafinadFlow | null => {
  if (!orderUuid) {
    return null;
  }

  try {
    const stored = window.sessionStorage.getItem(ORDER_TRACKING_STORAGE_KEY);

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as RafinadFlow & { order_uuid?: string };

    if (parsed.order_uuid !== orderUuid) {
      return null;
    }

    return {
      page_type: 'thanks',
      order_id: parsed.order_id,
      order_total: parsed.order_total,
      currency: parsed.currency,
      goal: parsed.goal,
      status: parsed.status
    };
  } catch {
    return null;
  }
};

const setRafinadFlow = (flow: RafinadFlow) => {
  (window as RafinadWindow).XG_FLOW = flow;
};

const getTrackingDomain = () => getCookie('domain_xg') || DEFAULT_TRACKING_DOMAIN;

const canSendOrderTracking = () => {
  const attribution = normalize(getCookie('utm_source'));

  return (
    attribution === 'rafinad' &&
    Boolean(getCookie('utm_source') && getCookie('utm_content'))
  );
};

const storeOrderTracking = (
  params: Required<Pick<RafinadOrderTrackingParams, 'orderId'>> &
    Omit<RafinadOrderTrackingParams, 'orderId'>
) => {
  try {
    window.sessionStorage.setItem(
      ORDER_TRACKING_STORAGE_KEY,
      JSON.stringify({
        page_type: 'thanks',
        order_uuid: params.orderUuid,
        order_id: params.orderId,
        order_total: params.orderTotal ? String(params.orderTotal) : undefined,
        currency: params.currency || RAFINAD_CURRENCY,
        goal: params.goal || RAFINAD_GOAL,
        status: params.status || RAFINAD_NEW_ORDER_STATUS
      })
    );
  } catch {
    return;
  }
};

const isRafinadVisit = (params: URLSearchParams) => {
  const source = normalize(params.get('utm_source'));
  const hasRafinadSource = RAFINAD_SOURCES.has(source);

  return hasRafinadSource;
};

const isPaidNonRafinadVisit = (params: URLSearchParams) => {
  const source = normalize(params.get('utm_source'));
  const medium = normalize(params.get('utm_medium'));

  if (RAFINAD_SOURCES.has(source)) {
    return false;
  }

  return (
    PAID_MEDIUMS.has(medium) ||
    PAID_SOURCES.has(source) ||
    Boolean(firstParam(params, ['gclid', 'yclid', 'fbclid', 'ttclid', 'admitad_uid']))
  );
};

const captureRafinadParams = (params: URLSearchParams) => {
  const domain = getCookieDomain();
  const source = normalize(params.get('utm_source'));

  if (source && !RAFINAD_SOURCES.has(source)) {
    clearRafinadCookies(domain);
    return;
  }

  if (!isRafinadVisit(params)) {
    if (isPaidNonRafinadVisit(params)) {
      clearRafinadCookies(domain);
    }

    return;
  }

  const days = getTtlDays(params);
  const clickId = firstParam(params, ['utm_content', 'click_id', 'click_id_xg']);
  const flowId = firstParam(params, ['utm_content', 'click_id', 'click_id_xg']);
  const values: Partial<Record<(typeof RAFINAD_COOKIE_NAMES)[number], string>> = {
    utm_source: firstParam(params, ['utm_source']) || 'rafinad',
    utm_medium: firstParam(params, ['utm_medium']),
    utm_campaign: firstParam(params, ['utm_campaign']),
    click_id: clickId,
    click_id_xg: clickId,
    flow_id: flowId,
    flow_id_xg: flowId,
    domain_xg: firstParam(params, ['domain_xg']),
    attribution_xg: firstParam(params, ['attribution_xg']) || 'xg'
  };

  clearRafinadCookies(domain);

  RAFINAD_COOKIE_NAMES.forEach((name) => {
    const value = values[name];

    if (value) {
      setCookie(name, value, days, domain);
    }
  });
};

export const sendRafinadOrderTracking = (params: RafinadOrderTrackingParams) => {
  if (!params.orderId) {
    return;
  }

  storeOrderTracking(params);

  if (!canSendOrderTracking()) {
    return;
  }

  const q = new URLSearchParams();
  const clickId = getCookie('utm_content');
  const flowId = getCookie('utm_content');
  const flow: RafinadFlow = {
    page_type: 'thanks',
    order_id: params.orderId,
    order_total: params.orderTotal ? String(params.orderTotal) : undefined,
    currency: params.currency || RAFINAD_CURRENCY,
    goal: params.goal || RAFINAD_GOAL,
    status: params.status || RAFINAD_NEW_ORDER_STATUS
  };

  if (clickId) {
    q.set('click_id', clickId);
  }

  if (flowId) {
    q.set('flow_id', flowId);
  }

  Object.entries(flow).forEach(([key, value]) => {
    if (key === 'page_type' || !value) {
      return;
    }

    q.set(key, value);
  });

  setRafinadFlow(flow);

  const trackingUrl = `${getTrackingDomain().replace(/\/+$/, '')}/api/tracking/?${q.toString()}`;
  new Image().src = trackingUrl;
};

export const RafinadAttribution = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const pageType = getPageType(pathname);
    const storedOrderTracking = getStoredOrderTracking(params.get('order_uuid'));

    setRafinadFlow(storedOrderTracking || { page_type: pageType });
    captureRafinadParams(params);
  }, [pathname, search]);

  return null;
};
